/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Col, Row, Typography } from 'antd';
import { ClockCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { BannerSliderSearcher } from '@/components/forms/banner-slider-searcher.form';
import { BannerSliderTable } from '@/components/tables/banner-slider.table';
import { useCallback, useState } from 'react';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { useAppDispatch } from '@/redux/hooks';
import { getListBanner } from '@/redux/slices/banner.slice';
import { BannerSliderModal } from '@/components/modals/banner-slider.modal';
import { useModal } from '@/hooks/modal.hook';
import { useDrawer } from '@/hooks/drawer.hook';
import { BannerSliderDrawer } from '@/components/drawer/banner-slider.drawer';
import { PageURLs } from '@/utils/navigate';
import { PermissionMode } from '@/enums/mode.enum';
import { useQuery } from '@/hooks/query.hook';
import { Button } from '@/components/button';
import usePermission from '@/hooks/permission.hook';

type PageChangeParams = {
  page?: number;
  pageSize?: number;
  filter?: FilterBanner;
};

interface BannerParams {
  offset: number;
  limit: number;
  sort_by: string;
  platform?: number;
  active?: number;
  name?: string;
}

export default function HomeBannerSlider() {
  const dispatch = useAppDispatch();
  const [filter, setFilter] = useState<FilterBanner>({});
  const { push } = useQuery();
  const { openModal, closeModal } = useModal();
  const { openDrawer } = useDrawer();
  const { creatable, updatable } = usePermission();

  const handlePageChange = useCallback(
    ({
      page = PAGINATION_PARAMS.offset,
      pageSize = PAGINATION_PARAMS.limit,
      filter
    }: PageChangeParams) => {
      const params: BannerParams = {
        offset: page,
        limit: pageSize,
        sort_by: 'ASC'
      };

      if (filter?.platform) {
        params.platform = filter.platform;
      }
      if (filter?.active !== undefined) {
        params.active = filter.active;
      }
      if (filter?.textSearch) {
        params.name = filter.textSearch;
      }

      dispatch(getListBanner(params));
    },
    [dispatch]
  );

  const handleSearch = (values: FilterBanner) => {
    const newFilter = {
      ...filter,
      ...values
    };
    setFilter(newFilter);
    handlePageChange({ filter: newFilter });
  };

  const handleOpenModal = () => {
    openModal(<BannerSliderModal onCancel={closeModal} />);
  };
  const handleOpenDrawer = () => {
    openDrawer(<BannerSliderDrawer />);
  };

  const handleAddBanner = () => {
    push(`${PageURLs.ofHomeBannerSlider()}?mode=${PermissionMode.CREATE}`);
  };

  return (
    <div css={rootStyles}>
      <Row css={boxStyles} justify='space-between' align='middle'>
        <Col>
          <Typography.Title css={titleStyles} level={5}>
            Quản lý Banner slider ở Trang chủ
          </Typography.Title>
        </Col>
        <Col>
          <Row justify='space-between' css={{ gap: '10px' }}>
            {updatable && (
              <>
                <Button
                  icon={<ClockCircleOutlined />}
                  iconPosition='start'
                  onClick={handleOpenModal}
                >
                  Cài đặt thời gian chuyển banner
                </Button>
                <Button
                  icon={<i className='bx bx-sort-alt-2'></i>}
                  iconPosition='start'
                  onClick={handleOpenDrawer}
                >
                  Sắp xếp thứ tự hiển thị banner
                </Button>
              </>
            )}
            {creatable && (
              <Button
                type='primary'
                icon={<PlusOutlined />}
                iconPosition='start'
                onClick={handleAddBanner}
              >
                Thêm mới banner
              </Button>
            )}
          </Row>
        </Col>
      </Row>
      <div css={boxStyles}>
        <BannerSliderSearcher onSearch={handleSearch} />
      </div>
      <div css={tableStyles}>
        <BannerSliderTable
          handleSearch={handleSearch}
          handlePageChange={handlePageChange}
          filter={filter}
        />
      </div>
    </div>
  );
}
const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
`;

const tableStyles = css`
  background: var(--white-color);
  border-radius: 1rem;
  padding: 0 1rem;
  .table-top {
    padding: 1rem 0;
  }
`;
